/**
 * Field Registry System
 * 
 * Manages detected fields and provides efficient lookup and filtering capabilities.
 * Handles dynamic content changes and maintains field lifecycle.
 */

import { FieldType, FieldDetectionResult } from './detection-patterns';

/**
 * Information about a detected field
 */
export interface FieldInfo {
  /** The HTML form element */
  element: HTMLInputElement | HTMLTextAreaElement;
  /** The data-name attribute value */
  dataName: string | null;
  /** Detected field type */
  fieldType: FieldType;
  /** Detection confidence and method */
  detection: FieldDetectionResult;
  /** Current field value */
  currentValue: string;
  /** Whether the field is currently active/focused */
  isActive: boolean;
  /** Whether formatting has been applied */
  formatApplied?: boolean;
  /** Timestamp when field was detected */
  detectedAt: Date;
  /** Whether the field is currently valid */
  isValid?: boolean;
  /** Last validation error message */
  lastError?: string;
}

/**
 * Registry statistics
 */
export interface RegistryStats {
  totalFields: number;
  fieldsByType: Record<FieldType, number>;
  fieldsWithHighConfidence: number;
  fieldsFormatted: number;
  lastScanTime?: Date;
}

/**
 * Field Registry Class
 * 
 * Central registry for managing all detected form fields that support formatting.
 * Provides efficient lookup, filtering, and lifecycle management.
 */
export class FieldRegistry {
  private fields: Map<HTMLElement, FieldInfo> = new Map();
  private observers: MutationObserver[] = [];
  private debugMode: boolean = false;
  private lastScanTime?: Date;
  private fieldAddedCallbacks: ((field: FieldInfo) => void)[] = [];
  private fieldRemovedCallbacks: ((field: FieldInfo) => void)[] = [];

  constructor(debugMode: boolean = false) {
    this.debugMode = debugMode;
  }

  /**
   * Register callback for when field is added
   */
  public onFieldAdded(callback: (field: FieldInfo) => void): void {
    this.fieldAddedCallbacks.push(callback);
  }

  /**
   * Register callback for when field is removed
   */
  public onFieldRemoved(callback: (field: FieldInfo) => void): void {
    this.fieldRemovedCallbacks.push(callback);
  }

  /**
   * Register a field in the registry
   * @param fieldInfo The field information to register
   */
  public registerField(fieldInfo: FieldInfo): void {
    try {
      this.fields.set(fieldInfo.element, fieldInfo);
      
      this.log(`Registered ${fieldInfo.fieldType} field: ${fieldInfo.dataName || 'unnamed'} (confidence: ${fieldInfo.detection.confidence})`);
      
      // Add event listeners for value tracking
      this.attachFieldListeners(fieldInfo);
      
      // Call registered callbacks
      this.fieldAddedCallbacks.forEach(callback => {
        try {
          callback(fieldInfo);
        } catch (error) {
          this.log(`Error in field added callback: ${error}`, 'error');
        }
      });
      
    } catch (error) {
      this.log(`Error registering field: ${error}`, 'error');
    }
  }

  /**
   * Remove a field from the registry
   * @param element The element to remove
   * @returns Boolean indicating if field was removed
   */
  public unregisterField(element: HTMLElement): boolean {
    const fieldInfo = this.fields.get(element);
    if (fieldInfo) {
      this.detachFieldListeners(fieldInfo);
      this.fields.delete(element);
      this.log(`Unregistered field: ${fieldInfo.dataName || 'unnamed'}`);
      
      // Call registered callbacks
      this.fieldRemovedCallbacks.forEach(callback => {
        try {
          callback(fieldInfo);
        } catch (error) {
          this.log(`Error in field removed callback: ${error}`, 'error');
        }
      });
      
      return true;
    }
    return false;
  }

  /**
   * Get field information for a specific element
   * @param element The form element
   * @returns Field information or undefined if not found
   */
  public getFieldInfo(element: HTMLElement): FieldInfo | undefined {
    return this.fields.get(element);
  }

  /**
   * Get all fields of a specific type
   * @param fieldType The field type to filter by
   * @returns Array of field information objects
   */
  public getAllFieldsByType(fieldType: FieldType): FieldInfo[] {
    return Array.from(this.fields.values())
      .filter(field => field.fieldType === fieldType);
  }

  /**
   * Get fields by detection confidence
   * @param minConfidence Minimum confidence threshold
   * @returns Array of field information objects
   */
  public getFieldsByConfidence(minConfidence: number): FieldInfo[] {
    return Array.from(this.fields.values())
      .filter(field => field.detection.confidence >= minConfidence);
  }

  /**
   * Get all registered fields
   * @returns Array of all field information objects
   */
  public getAllFields(): FieldInfo[] {
    return Array.from(this.fields.values());
  }

  /**
   * Get fields that have been formatted
   * @returns Array of formatted field information objects
   */
  public getFormattedFields(): FieldInfo[] {
    return Array.from(this.fields.values())
      .filter(field => field.formatApplied === true);
  }

  /**
   * Get fields that need formatting
   * @returns Array of field information objects that need formatting
   */
  public getUnformattedFields(): FieldInfo[] {
    return Array.from(this.fields.values())
      .filter(field => field.formatApplied !== true && field.fieldType !== FieldType.UNKNOWN);
  }

  /**
   * Update field value and status
   * @param element The form element
   * @param value New field value
   * @param isValid Validation status
   * @param error Optional error message
   */
  public updateField(element: HTMLElement, value: string, isValid?: boolean, error?: string): void {
    const fieldInfo = this.fields.get(element);
    if (fieldInfo) {
      fieldInfo.currentValue = value;
      if (isValid !== undefined) {
        fieldInfo.isValid = isValid;
      }
      if (error !== undefined) {
        fieldInfo.lastError = error;
      }
      
      this.log(`Updated field ${fieldInfo.dataName || 'unnamed'}: ${value}`);
    }
  }

  /**
   * Mark field as formatted
   * @param element The form element
   * @param formatted Whether formatting was applied
   */
  public setFieldFormatted(element: HTMLElement, formatted: boolean): void {
    const fieldInfo = this.fields.get(element);
    if (fieldInfo) {
      fieldInfo.formatApplied = formatted;
      this.log(`Marked field ${fieldInfo.dataName || 'unnamed'} as ${formatted ? 'formatted' : 'unformatted'}`);
    }
  }

  /**
   * Set field active state (focus tracking)
   * @param element The form element
   * @param isActive Whether the field is active
   */
  public setFieldActive(element: HTMLElement, isActive: boolean): void {
    const fieldInfo = this.fields.get(element);
    if (fieldInfo) {
      fieldInfo.isActive = isActive;
      this.log(`Field ${fieldInfo.dataName || 'unnamed'} ${isActive ? 'activated' : 'deactivated'}`);
    }
  }

  /**
   * Get registry statistics
   * @returns Registry statistics object
   */
  public getStats(): RegistryStats {
    const fields = this.getAllFields();
    const fieldsByType: Record<FieldType, number> = {
      [FieldType.PHONE]: 0,
      [FieldType.ZIP]: 0,
      [FieldType.DATE_OF_BIRTH]: 0,
      [FieldType.CURRENCY]: 0,
      [FieldType.SSN]: 0,
      [FieldType.NAME]: 0,
      [FieldType.UNKNOWN]: 0
    };

    let fieldsWithHighConfidence = 0;
    let fieldsFormatted = 0;

    fields.forEach(field => {
      fieldsByType[field.fieldType]++;
      
      if (field.detection.confidence >= 0.8) {
        fieldsWithHighConfidence++;
      }
      
      if (field.formatApplied) {
        fieldsFormatted++;
      }
    });

    return {
      totalFields: fields.length,
      fieldsByType,
      fieldsWithHighConfidence,
      fieldsFormatted,
      lastScanTime: this.lastScanTime
    };
  }

  /**
   * Start monitoring for dynamically added form elements
   */
  public startDynamicDetection(onNewFieldCallback?: (element: HTMLElement) => void): void {
    try {
      if (typeof MutationObserver === 'undefined') {
        this.log('MutationObserver not available, skipping dynamic detection', 'warn');
        return;
      }

      const observer = new MutationObserver(mutations => {
        mutations.forEach(mutation => {
          mutation.addedNodes.forEach(node => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as HTMLElement;
              
              // Check if the added element is a form field
              if (this.isFormField(element)) {
                this.log(`Detected new form field: ${element.tagName}`);
                if (onNewFieldCallback) {
                  onNewFieldCallback(element);
                }
              }
              
              // Check for form fields within the added element
              const childFields = element.querySelectorAll('input, textarea, select');
              childFields.forEach(childField => {
                if (this.isFormField(childField as HTMLElement)) {
                  this.log(`Detected new child form field: ${childField.tagName}`);
                  if (onNewFieldCallback) {
                    onNewFieldCallback(childField as HTMLElement);
                  }
                }
              });
            }
          });

          // Handle removed nodes for cleanup
          mutation.removedNodes.forEach(node => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as HTMLElement;
              
              // Clean up if this was a registered field
              if (this.fields.has(element)) {
                this.unregisterField(element);
              }
              
              // Clean up child fields
              if (element.querySelectorAll) {
                const childFields = element.querySelectorAll('input, textarea, select');
                childFields.forEach(childField => {
                  if (this.fields.has(childField as HTMLElement)) {
                    this.unregisterField(childField as HTMLElement);
                  }
                });
              }
            }
          });
        });
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true
      });

      this.observers.push(observer);
      this.log('Dynamic field detection started');

    } catch (error) {
      this.log(`Error starting dynamic detection: ${error}`, 'error');
    }
  }

  /**
   * Stop dynamic detection and cleanup
   */
  public stopDynamicDetection(): void {
    this.observers.forEach(observer => observer.disconnect());
    this.observers = [];
    this.log('Dynamic field detection stopped');
  }

  /**
   * Clear all registered fields and cleanup
   */
  public clear(): void {
    // Cleanup event listeners
    this.fields.forEach(fieldInfo => {
      this.detachFieldListeners(fieldInfo);
    });
    
    this.fields.clear();
    this.stopDynamicDetection();
    this.log('Registry cleared');
  }

  /**
   * Set last scan time (called by field detector)
   * @param scanTime The scan timestamp
   */
  public setLastScanTime(scanTime: Date): void {
    this.lastScanTime = scanTime;
  }

  /**
   * Get last scan time
   * @returns The last scan timestamp
   */
  public getLastScanTime(): Date | undefined {
    return this.lastScanTime;
  }

  /**
   * Get registered field count
   * @returns Number of registered fields
   */
  public getRegisteredFieldCount(): number {
    return this.fields.size;
  }

  /**
   * Check if element is a form field
   * @param element Element to check
   * @returns Boolean indicating if it's a form field
   */
  private isFormField(element: HTMLElement): boolean {
    // Skip fields inside outcome components (e.g. the additional-info form) —
    // they are owned by AdditionalInfoFormController, not the shared formatter.
    if (typeof element.closest === 'function' &&
        element.closest('[data-no-format="true"], [data-component*="outcome"]')) {
      return false;
    }
    const tagName = element.tagName.toLowerCase();
    return tagName === 'input' || tagName === 'textarea' || tagName === 'select';
  }

  /**
   * Attach event listeners to track field changes
   * @param fieldInfo The field to attach listeners to
   */
  private attachFieldListeners(fieldInfo: FieldInfo): void {
    const element = fieldInfo.element;

    // Value change tracking
    const handleValueChange = () => {
      this.updateField(element, element.value);
    };

    // Focus tracking
    const handleFocus = () => {
      this.setFieldActive(element, true);
    };

    const handleBlur = () => {
      this.setFieldActive(element, false);
    };

    element.addEventListener('input', handleValueChange);
    element.addEventListener('change', handleValueChange);
    element.addEventListener('focus', handleFocus);
    element.addEventListener('blur', handleBlur);

    // Store cleanup functions
    (element as any).__registryCleanup = () => {
      element.removeEventListener('input', handleValueChange);
      element.removeEventListener('change', handleValueChange);
      element.removeEventListener('focus', handleFocus);
      element.removeEventListener('blur', handleBlur);
    };
  }

  /**
   * Detach event listeners from field
   * @param fieldInfo The field to detach listeners from
   */
  private detachFieldListeners(fieldInfo: FieldInfo): void {
    const element = fieldInfo.element;
    const cleanup = (element as any).__registryCleanup;
    
    if (cleanup && typeof cleanup === 'function') {
      cleanup();
      delete (element as any).__registryCleanup;
    }
  }

  /**
   * Log messages (with debug mode check)
   * @param message The message to log
   * @param level Log level
   */
  private log(message: string, level: 'log' | 'warn' | 'error' = 'log'): void {
    if (this.debugMode) {
      console[level](`[FieldRegistry] ${message}`);
    }
  }
}

/**
 * Singleton registry instance
 */
let registryInstance: FieldRegistry | null = null;

/**
 * Get or create the global field registry instance
 * @param debugMode Whether to enable debug logging
 * @returns The field registry instance
 */
export function getFieldRegistry(debugMode: boolean = false): FieldRegistry {
  if (!registryInstance) {
    registryInstance = new FieldRegistry(debugMode);
  }
  return registryInstance;
}

/**
 * Reset the global registry instance (primarily for testing)
 */
export function resetFieldRegistry(): void {
  if (registryInstance) {
    registryInstance.clear();
  }
  registryInstance = null;
}